import {
  pendingSourcesSpotlightAfterStreamAtom,
  sessionChatSidebarSpotlightOpenAtom,
  sourcesSpotlightOpenAtom,
} from '@/atoms/evidenceOnboardingAtoms';
import { showTranscribeOnboardingPopoverAtom } from '@/atoms/freeTrialModalAtoms';
import {
  menuCoachmarkStepAtom,
  menuCoachmarkWelcomeOpenAtom,
} from '@/atoms/menuCoachmarkAtoms';
import {
  sidebarPanelContentAtom,
  sidebarPanelPinnedAtom,
} from '@/atoms/sidebarPanelAtoms';
import { scribeSpotlightOpenAtom } from '@/atoms/sideNavAtoms';
import { useQueuedCoachmarkTour } from '@/hooks/useQueuedCoachmarkTour';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useMemo, useRef } from 'react';

/** Delay before showing the welcome spotlight to let the user orient themselves */
const WELCOME_DELAY_MS = 1500;

/**
 * Hook that manages the menu coachmark tour auto-trigger.
 *
 * Call this once in a high-level layout component (e.g., ResizableLayout).
 * It will auto-open the welcome spotlight when the modal queue indicates
 * it's the coachmark tour's turn, after a short delay to let the user
 * orient themselves.
 *
 * If another spotlight is active when the queue permits, the hook
 * will automatically retry once that spotlight closes — no user action needed.
 *
 * Individual step components (MenuCoachmarkPinStep, MenuCoachmarkHoverStep, etc.)
 * are self-contained and manage their own state via atoms.
 *
 * Returns whether the tour is currently active, for use in coordination checks.
 */
export function useMenuCoachmark() {
  const welcomeOpen = useAtomValue(menuCoachmarkWelcomeOpenAtom);
  const setWelcomeOpen = useSetAtom(menuCoachmarkWelcomeOpenAtom);
  const currentStep = useAtomValue(menuCoachmarkStepAtom);
  const setIsPinned = useSetAtom(sidebarPanelPinnedAtom);
  const setHoverContent = useSetAtom(sidebarPanelContentAtom);

  // Queue subscription - tour is active when it's the current modal in queue
  const { isQueueActive } = useQueuedCoachmarkTour();

  // Other spotlight coordination atoms (read-only)
  const scribeSpotlightOpen = useAtomValue(scribeSpotlightOpenAtom);
  const sessionChatSidebarOpen = useAtomValue(sessionChatSidebarSpotlightOpenAtom);
  const sourcesSpotlightOpen = useAtomValue(sourcesSpotlightOpenAtom);
  const pendingSourcesSpotlight = useAtomValue(pendingSourcesSpotlightAfterStreamAtom);
  const transcribeOnboardingOpen = useAtomValue(showTranscribeOnboardingPopoverAtom);

  // Prevents race condition: once the welcome has been shown this session,
  // don't re-trigger even if there's a brief window between tour completion
  // and the seen_modals mutation propagating back.
  const hasShownRef = useRef(false);

  const isOtherSpotlightActive = useMemo(
    () =>
      scribeSpotlightOpen ||
      sessionChatSidebarOpen ||
      sourcesSpotlightOpen ||
      pendingSourcesSpotlight ||
      transcribeOnboardingOpen,
    [
      scribeSpotlightOpen,
      sessionChatSidebarOpen,
      sourcesSpotlightOpen,
      pendingSourcesSpotlight,
      transcribeOnboardingOpen,
    ]
  );

  const isTourActive = welcomeOpen || currentStep !== null;

  // Reset tour state if queue loses priority (e.g., a higher-priority modal opens).
  // This prevents the tour from appearing "stuck" in an open state when it's no
  // longer the active modal in the queue.
  const setStep = useSetAtom(menuCoachmarkStepAtom);
  useEffect(() => {
    if (!isQueueActive && isTourActive) {
      // Clear hasShownRef so the tour can restart when queue becomes active again
      hasShownRef.current = false;
      setWelcomeOpen(false);
      setStep(null);
    }
  }, [isQueueActive, isTourActive, setWelcomeOpen, setStep]);

  // Auto-open the welcome spotlight when the queue gives us permission.
  //
  // Reactive retry: if another spotlight is active, the effect early-returns
  // without setting hasShownRef. When that spotlight closes,
  // isOtherSpotlightActive changes → effect re-runs → delay starts.
  //
  // If conditions change during the delay (e.g., a spotlight opens),
  // the cleanup function clears the timer, and the next effect evaluation
  // will early-return. When conditions are met again, a fresh delay starts.
  useEffect(() => {
    if (hasShownRef.current) return;
    if (!isQueueActive) return; // Wait for queue permission
    if (isOtherSpotlightActive) return;
    if (welcomeOpen || currentStep !== null) return;

    const timeoutId = window.setTimeout(() => {
      hasShownRef.current = true;

      // Ensure panel is pinned and visible for welcome
      setIsPinned(true);
      setHoverContent(null);

      // Open welcome spotlight
      setWelcomeOpen(true);
    }, WELCOME_DELAY_MS);

    return () => window.clearTimeout(timeoutId);
  }, [
    isQueueActive,
    isOtherSpotlightActive,
    welcomeOpen,
    currentStep,
    setIsPinned,
    setHoverContent,
    setWelcomeOpen,
  ]);

  return { isTourActive };
}
